'use client';

import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { MENU } from '../../shared/menu';
import { logout } from '../../store/actions/auth';
import AlertBox from './AlertBox';
import socket from '../../socket';

const Header = () => {
  const dispatch = useDispatch();
  const [activeProfile, setActiveProfile] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);
  const [openSubMenu, setOpenSubMenu] = useState(null);
  const [openSubSubMenu, setOpenSubSubMenu] = useState(null);
  const [desktopOpenDropdown, setDesktopOpenDropdown] = useState(null);
  const [desktopOpenSubDropdown, setDesktopOpenSubDropdown] = useState(null);
  const [unreadMessages, setUnreadMessages] = useState(0);

  const data = useSelector((state) => state.auth);
  const { user } = data;
  const location = useLocation();

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.nav-item.dropdown')) {
        setDesktopOpenDropdown(null);
        setDesktopOpenSubDropdown(null);
      }
      if (!event.target.closest('.profile-dropdown')) {
        setActiveProfile(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Listen to chat unread messages
  useEffect(() => {
    const handleReceiveMessage = (message) => {
      // Increment unread count if not on chat page
      if (location.pathname !== '/xchat' && message.from !== user?._id) {
        setUnreadMessages((prev) => prev + 1);
      }
    };

    const handleMessagesRead = () => {
      // Reset unread count when viewing chat
      if (location.pathname === '/xchat') {
        setUnreadMessages(0);
      }
    };

    socket.on('receive-message', handleReceiveMessage);
    socket.on('messages-read', handleMessagesRead);

    return () => {
      socket.off('receive-message', handleReceiveMessage);
      socket.off('messages-read', handleMessagesRead);
    };
  }, [user?._id, location.pathname]);

  // Reset unread when navigating to chat
  useEffect(() => {
    if (location.pathname === '/xchat') {
      setUnreadMessages(0);
    }
  }, [location.pathname]);

  const logoutUser = () => {
    dispatch(logout());
  };

  // Helper to check if a menu or submenu is active
  const isActive = (link) => {
    if (!link || link === '#') return false;
    return (
      location.pathname === link || location.pathname.startsWith(link + '/')
    );
  };

  return (
    <>
      <style jsx>{`
        /* Clean White Header */
        #page-topbar {
          background: white;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border-bottom: 1px solid #e9ecef;
          position: relative;
          z-index: 1001;
          padding: 0;
        }

        .navbar-header {
          padding: 8px 20px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          min-height: 50px;
        }

        .navbar-brand-box {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .navbar-brand-box .logo img {
          transition: transform 0.2s ease;
        }

        .navbar-brand-box .logo:hover img {
          transform: scale(1.02);
        }

        /* Mobile Menu Button */
        .header-item {
          background: #f8f9fa;
          border: 1px solid #dee2e6;
          color: #495057;
          border-radius: 6px;
          padding: 6px 10px;
          transition: all 0.2s ease;
          font-size: 12px;
        }

        .header-item:hover {
          background: #e9ecef;
          color: #212529;
        }

        /* Profile Dropdown */
        .profile-dropdown .btn {
          background: white;
          border: 1px solid #dee2e6;
          color: #495057;
          border-radius: 20px;
          padding: 4px 12px;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s ease;
          font-size: 13px;
        }

        .profile-dropdown .btn:hover {
          background: #f8f9fa;
          border-color: #adb5bd;
          color: #212529;
        }

        .header-profile-user {
          width: 28px;
          height: 28px;
          border: 1px solid #dee2e6;
          transition: all 0.2s ease;
        }

        .profile-dropdown .btn:hover .header-profile-user {
          border-color: #adb5bd;
        }

        /* Clean Navigation Bar */
        .topnav {
          background: white;
          border-bottom: 1px solid #e9ecef;
          position: relative;
          z-index: 1000;
        }

        .topnav-menu {
          padding: 0 20px;
          min-height: 45px;
        }

        .navbar-nav {
          gap: 2px;
        }

        .navbar-nav .nav-link {
          color: #6c757d;
          font-weight: 500;
          font-size: 13px;
          padding: 8px 14px;
          border-radius: 6px;
          transition: all 0.2s ease;
          position: relative;
          text-decoration: none;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .navbar-nav .nav-link:hover {
          color: #dc2626;
          background: #fef2f2;
        }

        .navbar-nav .nav-item.active > .nav-link,
        .navbar-nav .nav-link.active {
          color: white;
          background: #dc2626;
          font-weight: 600;
        }

        /* Enhanced Dropdown Container - Key Fix */
        .nav-item.dropdown {
          position: relative;
        }

        /* Clean Dropdown Menus with Better Positioning */
        .dropdown-menu {
          background: white;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          padding: 4px;
          min-width: 160px;
          z-index: 9999;
          position: absolute;
          top: 100%;
          left: 0;
          margin-top: 2px;
          opacity: 0;
          visibility: hidden;
          transform: translateY(-5px);
          transition: all 0.2s ease;
          display: none;
        }

        .dropdown-menu.show {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
          display: block;
        }

        /* Remove hover bridge and effects */
        .nav-item.dropdown::before {
          display: none;
        }

        /* Nested Dropdowns with Better Positioning - Below instead of right */
        .dropdown .dropdown {
          position: relative;
        }

        .dropdown .dropdown-menu {
          top: 100%;
          left: 0;
          margin-left: 0;
          margin-top: 2px;
        }

        /* Remove nested dropdown hover bridge */
        .dropdown .dropdown::before {
          display: none;
        }

        .dropdown-item {
          padding: 6px 10px;
          color: #495057;
          text-decoration: none;
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          font-weight: 400;
          font-size: 12px;
          border-radius: 4px;
          transition: all 0.15s ease;
          border: 0;
          background: transparent;
        }

        .dropdown-item:hover {
          background: #f8f9fa;
          color: #dc2626;
        }

        .dropdown-item.active {
          background: #dc2626;
          color: white;
        }

        .dropdown-item i {
          width: 14px;
          text-align: center;
          margin-right: 6px;
          font-size: 11px;
        }

        /* Chevron icon styling */
        .nav-link i,
        .dropdown-item i.fa-chevron-right,
        .dropdown-item i.fa-chevron-up,
        .dropdown-item i.fa-chevron-down {
          opacity: 0.7;
          transition: opacity 0.2s ease;
          flex-shrink: 0;
        }

        .nav-link:hover i,
        .dropdown-item:hover i {
          opacity: 1;
        }

        /* Better spacing for dropdown items with icons */
        .dropdown-item span {
          flex: 1;
        }

        /* Active state improvements */
        .navbar-nav .nav-item.active > .nav-link i {
          color: white;
          opacity: 1;
        }

        .dropdown-divider {
          height: 1px;
          background: #e9ecef;
          margin: 4px 0;
          border: 0;
        }

        /* Nested Dropdowns with Better Positioning - Below instead of right */
        .dropdown .dropdown {
          position: relative;
        }

        .dropdown .dropdown-menu {
          top: 100%;
          left: 0;
          margin-left: 0;
          margin-top: 2px;
        }

        /* Remove nested dropdown hover bridge */
        .dropdown .dropdown::before {
          display: none;
        }

        /* Profile Dropdown Positioning */
        .profile-dropdown {
          position: relative;
        }

        .profile-dropdown .dropdown-menu {
          right: 0;
          left: auto;
          top: 100%;
          min-width: 140px;
          margin-top: 2px;
        }

        /* Mobile Responsive */
        @media (max-width: 991.98px) {
          .navbar-header {
            padding: 6px 15px;
            min-height: 45px;
          }

          .topnav-menu {
            padding: 0 15px;
            min-height: 40px;
          }

          .dropdown-menu {
            position: static;
            float: none;
            width: auto;
            margin-top: 0;
            background-color: #f8f9fa;
            border: 0;
            box-shadow: none;
            border-radius: 0;
            opacity: 1;
            visibility: visible;
            transform: none;
            padding-left: 20px;
          }

          .navbar-nav .nav-link {
            padding: 10px 14px;
            border-radius: 0;
          }

          .mobile-only {
            display: block;
          }

          .desktop-only {
            display: none;
          }

          /* Remove hover effects on mobile - already handled by click */
          .nav-item.dropdown:hover .dropdown-menu,
          .dropdown .dropdown:hover > .dropdown-menu,
          .profile-dropdown:hover .dropdown-menu {
            opacity: 0;
            visibility: hidden;
            display: none;
          }

          /* Nested dropdowns on mobile also appear below */
          .dropdown .dropdown-menu {
            margin-left: 15px;
          }
        }

        @media (min-width: 992px) {
          .mobile-only {
            display: none;
          }

          .desktop-only {
            display: block;
          }
        }

        /* Logo Positioning */
        .d-flex .navbar-brand-box:first-child {
          margin-right: auto;
        }

        .d-flex .navbar-brand-box:nth-child(2) {
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
        }

        /* Compact Logo Sizes */
        .logo img {
          height: 35px !important;
        }

        .navbar-brand-box:nth-child(2) .logo img {
          height: 45px !important;
        }

        /* Profile Name Styling */
        .fw-medium {
          font-size: 13px;
          font-weight: 500;
        }

        /* New Badge Styling */
        .badge-new {
          background: linear-gradient(45deg, #dc2626, #ef4444) !important;
          color: white !important;
          font-weight: 600 !important;
          font-size: 8px !important;
          padding: 2px 5px !important;
          border-radius: 8px !important;
          margin-left: 6px !important;
          text-transform: uppercase !important;
          letter-spacing: 0.5px !important;
          box-shadow: 0 1px 3px rgba(220, 38, 38, 0.3) !important;
          animation: pulse-new 2s infinite !important;
        }

        @keyframes pulse-new {
          0% {
            box-shadow: 0 1px 3px rgba(220, 38, 38, 0.3);
          }
          50% {
            box-shadow: 0 1px 6px rgba(220, 38, 38, 0.5);
          }
          100% {
            box-shadow: 0 1px 3px rgba(220, 38, 38, 0.3);
          }
        }

        /* Clean focus states */
        .nav-link:focus,
        .dropdown-item:focus,
        .header-item:focus,
        .profile-dropdown .btn:focus {
          outline: 2px solid #dc2626;
          outline-offset: 2px;
        }

        /* Enhanced Mobile Menu */
        .collapse.show {
          background: white;
          border-radius: 0 0 8px 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }
      `}</style>

      <div>
        {user && (
          <div id='layout-wrapper'>
            <header id='page-topbar'>
              <div className='navbar-header'>
                <div className='d-flex'>
                  <div className='navbar-brand-box'>
                    <Link to='/dashboard' className='logo logo-dark'>
                      <span className='logo-sm'>
                        <img src='/images/logo.png' height={35} alt='Logo' />
                      </span>
                      <span className='logo-lg'>
                        <img src='/images/logo.png' height={35} alt='Logo' />
                      </span>
                    </Link>
                    <Link to='/dashboard' className='logo logo-light'>
                      <span className='logo-sm'>
                        <img src='/images/logo.png' height={35} alt='Logo' />
                      </span>
                      <span className='logo-lg'>
                        <img src='/images/logo.png' height={35} alt='Logo' />
                      </span>
                    </Link>
                  </div>

                  <div className='navbar-brand-box'>
                    <Link to='/dashboard' className='logo logo-dark'>
                      <span className='logo-sm'>
                        <img
                          src='/images/ld-premier-league.png'
                          height={45}
                          alt='Premier League Logo'
                        />
                      </span>
                      <span className='logo-lg'>
                        <img
                          src='/images/ld-premier-league.png'
                          height={45}
                          alt='Premier League Logo'
                        />
                      </span>
                    </Link>
                    <Link to='/dashboard' className='logo logo-light'>
                      <span className='logo-sm'>
                        <img
                          src='/images/ld-premier-league.png'
                          height={45}
                          alt='Premier League Logo'
                        />
                      </span>
                      <span className='logo-lg'>
                        <img
                          src='/images/ld-premier-league.png'
                          height={45}
                          alt='Premier League Logo'
                        />
                      </span>
                    </Link>
                  </div>

                  <button
                    type='button'
                    className='btn btn-sm px-3 font-size-16 d-lg-none header-item waves-effect waves-light'
                    data-bs-toggle='collapse'
                    data-bs-target='#topnav-menu-content'
                    onClick={() => setOpenMenu(!openMenu)}
                  >
                    <i className='fa fa-fw fa-bars' />
                  </button>
                </div>

                <div className='d-flex'>
                  <div className='dropdown d-inline-block profile-dropdown'>
                    <button
                      type='button'
                      className='btn header-item'
                      id='page-header-user-dropdown'
                      data-bs-toggle='dropdown'
                      aria-haspopup='true'
                      aria-expanded='false'
                      onClick={() => setActiveProfile(!activeProfile)}
                    >
                      <img
                        className='rounded-circle header-profile-user'
                        src='/images/user.png'
                        alt='Header Avatar'
                      />
                      <span className='d-none d-xl-inline-block ms-1 fw-medium'>
                        {user && user.name}
                      </span>
                      <i className='fa fa-angle-down d-none d-xl-inline-block' />
                    </button>

                    <div
                      className={
                        activeProfile
                          ? 'dropdown-menu dropdown-menu-end show'
                          : 'dropdown-menu dropdown-menu-end'
                      }
                    >
                      <Link className='dropdown-item' to='/profile'>
                        <i className='fa fa-user font-size-16 align-middle me-1' />
                        Profile
                      </Link>
                      <div className='dropdown-divider' />
                      <button
                        style={{ cursor: 'pointer' }}
                        className='dropdown-item'
                        onClick={() => logoutUser()}
                      >
                        <i className='fa fa-lock font-size-16 align-middle me-1' />
                        Logout
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </header>

            <div className='topnav'>
              <div className='container-fluid'>
                <nav className='navbar navbar-light navbar-expand-lg topnav-menu desktop-only'>
                  <div
                    className={
                      openMenu
                        ? 'collapse navbar-collapse show'
                        : 'collapse navbar-collapse'
                    }
                    id='topnav-menu-content'
                  >
                    <ul className='navbar-nav'>
                      {MENU &&
                        MENU.map((sub_nav, index) => {
                          if (user.role && sub_nav.roles.includes(user.role)) {
                            const active = isActive(sub_nav.link);
                            return (
                              <li
                                key={index}
                                className={`nav-item dropdown${
                                  active ? ' active' : ''
                                }`}
                              >
                                <Link
                                  className={`nav-link arrow-none${
                                    active ? ' active' : ''
                                  }`}
                                  to={sub_nav.menu ? '#' : sub_nav.link}
                                  id='topnav-dashboard'
                                  role='button'
                                  onClick={(e) => {
                                    if (sub_nav.menu) {
                                      e.preventDefault();
                                      setDesktopOpenDropdown(
                                        desktopOpenDropdown === sub_nav.label
                                          ? null
                                          : sub_nav.label
                                      );
                                    } else {
                                      setDesktopOpenDropdown(null);
                                    }
                                  }}
                                >
                                  <span data-key='t-dashboards'>
                                    {sub_nav.label}
                                    {sub_nav.badge && (
                                      <span className='badge badge-new'>
                                        {sub_nav.label === 'Chat' && unreadMessages > 0
                                          ? unreadMessages
                                          : sub_nav.badge}
                                      </span>
                                    )}
                                  </span>
                                  {sub_nav.menu && (
                                    <i
                                      className={`fa fa-chevron-${
                                        desktopOpenDropdown === sub_nav.label
                                          ? 'up'
                                          : 'down'
                                      } ms-1`}
                                      style={{ fontSize: '10px' }}
                                    />
                                  )}
                                </Link>

                                {sub_nav.menu && (
                                  <div
                                    className={`dropdown-menu${
                                      desktopOpenDropdown === sub_nav.label
                                        ? ' show'
                                        : ''
                                    }`}
                                    style={{
                                      display:
                                        desktopOpenDropdown === sub_nav.label
                                          ? 'block'
                                          : 'none',
                                    }}
                                  >
                                    {sub_nav.menu &&
                                      sub_nav.menu.map(
                                        (sub_sub_nav, subIndex) => {
                                          if (
                                            user.role &&
                                            sub_sub_nav.roles.includes(
                                              user.role
                                            )
                                          ) {
                                            const subActive = isActive(
                                              sub_sub_nav.link
                                            );
                                            return (
                                              <div
                                                key={subIndex}
                                                className='dropdown'
                                              >
                                                <Link
                                                  className={`dropdown-item${
                                                    subActive ? ' active' : ''
                                                  }`}
                                                  to={
                                                    sub_sub_nav.menu
                                                      ? '#'
                                                      : sub_sub_nav.link
                                                  }
                                                  onClick={(e) => {
                                                    if (sub_sub_nav.menu) {
                                                      e.preventDefault();
                                                      setDesktopOpenSubDropdown(
                                                        desktopOpenSubDropdown ===
                                                          sub_sub_nav.label
                                                          ? null
                                                          : sub_sub_nav.label
                                                      );
                                                    } else {
                                                      setDesktopOpenSubDropdown(
                                                        null
                                                      );
                                                    }
                                                  }}
                                                >
                                                  <span data-key='t-dashboards'>
                                                    {sub_sub_nav.label}
                                                    {sub_sub_nav.badge && (
                                                      <span className='badge badge-new'>
                                                        {sub_sub_nav.badge}
                                                      </span>
                                                    )}
                                                  </span>
                                                  {sub_sub_nav.menu && (
                                                    <i
                                                      className={`fa fa-chevron-${
                                                        desktopOpenSubDropdown ===
                                                        sub_sub_nav.label
                                                          ? 'up'
                                                          : 'down'
                                                      } ms-auto`}
                                                      style={{
                                                        fontSize: '10px',
                                                      }}
                                                    />
                                                  )}
                                                </Link>
                                                {sub_sub_nav.menu && (
                                                  <div
                                                    className={`dropdown-menu${
                                                      desktopOpenSubDropdown ===
                                                      sub_sub_nav.label
                                                        ? ' show'
                                                        : ''
                                                    }`}
                                                    style={{
                                                      display:
                                                        desktopOpenSubDropdown ===
                                                        sub_sub_nav.label
                                                          ? 'block'
                                                          : 'none',
                                                    }}
                                                  >
                                                    {sub_sub_nav.menu.map(
                                                      (item, itemIndex) => {
                                                        const subSubActive =
                                                          isActive(item.link);
                                                        return (
                                                          <Link
                                                            key={itemIndex}
                                                            className={`dropdown-item${
                                                              subSubActive
                                                                ? 'active'
                                                                : ''
                                                            }`}
                                                            to={item.link}
                                                          >
                                                            <span data-key='t-dashboards'>
                                                              {item.label}
                                                            </span>
                                                          </Link>
                                                        );
                                                      }
                                                    )}
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          }
                                          return null;
                                        }
                                      )}
                                  </div>
                                )}
                              </li>
                            );
                          }
                          return null;
                        })}
                    </ul>
                  </div>
                </nav>

                <nav className='navbar navbar-light navbar-expand-lg topnav-menu mobile-only'>
                  <div
                    className={
                      openMenu
                        ? 'collapse navbar-collapse show'
                        : 'collapse navbar-collapse'
                    }
                    id='topnav-menu-content'
                  >
                    <ul className='navbar-nav'>
                      {MENU &&
                        MENU.map((sub_nav, index) => {
                          if (user.role && sub_nav.roles.includes(user.role)) {
                            const active = isActive(sub_nav.link);
                            return (
                              <li
                                key={index}
                                className={`nav-item dropdown${
                                  active ? ' active' : ''
                                }`}
                              >
                                <Link
                                  className={`nav-link arrow-none${
                                    active ? ' active' : ''
                                  }`}
                                  to={sub_nav.menu ? '#' : sub_nav.link}
                                  id='topnav-dashboard'
                                  role='button'
                                  onClick={(e) => {
                                    if (sub_nav.menu) {
                                      e.preventDefault();
                                      setOpenSubMenu(
                                        openSubMenu === sub_nav.label
                                          ? null
                                          : sub_nav.label
                                      );
                                    } else {
                                      setOpenSubMenu(null);
                                    }
                                  }}
                                >
                                  <span data-key='t-dashboards'>
                                    {sub_nav.label}
                                    {sub_nav.badge && (
                                      <span className='badge badge-new'>
                                        {sub_nav.label === 'Chat' && unreadMessages > 0
                                          ? unreadMessages
                                          : sub_nav.badge}
                                      </span>
                                    )}
                                  </span>
                                  {sub_nav.menu && (
                                    <i
                                      className={`fa fa-chevron-${
                                        openSubMenu === sub_nav.label
                                          ? 'up'
                                          : 'down'
                                      } ms-1`}
                                      style={{ fontSize: '10px' }}
                                    />
                                  )}
                                </Link>

                                {sub_nav.menu && (
                                  <div
                                    className='dropdown-menu'
                                    style={{
                                      display:
                                        openSubMenu === sub_nav.label
                                          ? 'block'
                                          : 'none',
                                    }}
                                  >
                                    {sub_nav.menu &&
                                      sub_nav.menu.map(
                                        (sub_sub_nav, subIndex) => {
                                          if (
                                            user.role &&
                                            sub_sub_nav.roles.includes(
                                              user.role
                                            )
                                          ) {
                                            const subActive = isActive(
                                              sub_sub_nav.link
                                            );
                                            return (
                                              <div
                                                key={subIndex}
                                                className='dropdown'
                                              >
                                                <Link
                                                  className={`dropdown-item${
                                                    subActive ? ' active' : ''
                                                  }`}
                                                  to={
                                                    sub_sub_nav.menu
                                                      ? '#'
                                                      : sub_sub_nav.link
                                                  }
                                                  onClick={(e) => {
                                                    if (sub_sub_nav.menu) {
                                                      e.preventDefault();
                                                      setOpenSubSubMenu(
                                                        openSubSubMenu ===
                                                          sub_sub_nav.label
                                                          ? null
                                                          : sub_sub_nav.label
                                                      );
                                                    } else {
                                                      setOpenSubSubMenu(null);
                                                    }
                                                  }}
                                                >
                                                  <span data-key='t-dashboards'>
                                                    {sub_sub_nav.label}
                                                    {sub_sub_nav.badge && (
                                                      <span className='badge badge-new'>
                                                        {sub_sub_nav.badge}
                                                      </span>
                                                    )}
                                                  </span>
                                                  {sub_sub_nav.menu && (
                                                    <i
                                                      className={`fa fa-chevron-${
                                                        openSubSubMenu ===
                                                        sub_sub_nav.label
                                                          ? 'up'
                                                          : 'down'
                                                      } ms-auto`}
                                                      style={{
                                                        fontSize: '10px',
                                                      }}
                                                    />
                                                  )}
                                                </Link>
                                                {sub_sub_nav.menu && (
                                                  <div
                                                    className='dropdown-menu'
                                                    style={{
                                                      display:
                                                        openSubSubMenu ===
                                                        sub_sub_nav.label
                                                          ? 'block'
                                                          : 'none',
                                                    }}
                                                  >
                                                    {sub_sub_nav.menu.map(
                                                      (item, itemIndex) => {
                                                        return (
                                                          <div key={itemIndex}>
                                                            <Link
                                                              className='dropdown-item'
                                                              to={item.link}
                                                            >
                                                              <span data-key='t-dashboards'>
                                                                {item.label}
                                                              </span>
                                                            </Link>
                                                          </div>
                                                        );
                                                      }
                                                    )}
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          }
                                          return null;
                                        }
                                      )}
                                  </div>
                                )}
                              </li>
                            );
                          }
                          return null;
                        })}
                    </ul>
                  </div>
                </nav>
              </div>
            </div>
            <div className='main-content' id='miniaresult' />
          </div>
        )}
        <AlertBox />
      </div>
    </>
  );
};

export default Header;
